import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  CircleDashed,
  Clock,
  Loader2,
  Radar,
  RefreshCw,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

type HealthRunStatus = "running" | "success" | "failure" | "skipped" | string;
type SlackStatus = "not_required" | "pending" | "posted" | "failed" | string;

type HealthSummary = {
  strictSuccess?: boolean;
  failureReasons?: string[];
  appAccess?: {
    previewClassification?: string | null;
    previewHttpStatus?: number | null;
    previewHasTitle?: boolean | null;
    reason?: string | null;
  };
};

type HealthRun = {
  id: string;
  slot_index: number;
  scheduled_for: string;
  status: HealthRunStatus;
  summary: HealthSummary | null;
  error_message: string | null;
  slack_status: SlackStatus;
  slack_error: string | null;
  started_at: string;
  completed_at: string | null;
  created_at: string;
};

type HealthSlot = {
  slot_index: number;
  slot_hour_utc: number;
  slot_utc_label: string;
  check_enabled: boolean;
  positive_enabled: boolean;
  updated_at: string;
  last_run: HealthRun | null;
  recent_runs: HealthRun[];
};

type HealthStatusPayload = {
  ok?: boolean;
  error?: string;
  slots?: HealthSlot[];
  generated_at?: string;
};

function providerText(value?: string | null): string {
  return (value ?? "").replace(/base44/gi, "Provider");
}

function formatDate(value?: string | null): string {
  if (!value) return "Not yet";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function statusLabel(status?: HealthRunStatus | null): string {
  if (!status) return "Not run";
  if (status === "success") return "Passed";
  if (status === "failure") return "Failed";
  if (status === "skipped") return "Skipped";
  if (status === "running") return "Running";
  return providerText(status.replace(/_/g, " "));
}

function statusClass(status?: HealthRunStatus | null): string {
  if (status === "success") return "border-emerald-500/40 bg-emerald-500/10 text-emerald-700";
  if (status === "failure") return "border-destructive/40 bg-destructive/10 text-destructive";
  if (status === "running") return "border-blue-500/40 bg-blue-500/10 text-blue-800";
  if (status === "skipped") return "border-border bg-muted text-muted-foreground";
  return "border-border bg-background text-muted-foreground";
}

function StatusIcon({ status }: { status?: HealthRunStatus | null }) {
  if (status === "success") return <CheckCircle2 className="h-3.5 w-3.5" />;
  if (status === "failure") return <AlertTriangle className="h-3.5 w-3.5" />;
  if (status === "running") return <Loader2 className="h-3.5 w-3.5 animate-spin" />;
  return <CircleDashed className="h-3.5 w-3.5" />;
}

function slackLabel(status?: SlackStatus | null): string {
  if (status === "posted") return "Slack posted";
  if (status === "failed") return "Slack failed";
  if (status === "pending") return "Slack pending";
  return "No Slack";
}

function runIssue(run: HealthRun | null): string | null {
  if (!run) return null;
  const firstReason = run.summary?.failureReasons?.find(Boolean);
  return providerText(firstReason ?? run.error_message ?? run.summary?.appAccess?.reason ?? "");
}

export function ProviderHealthChecks() {
  const { toast } = useToast();
  const [slots, setSlots] = useState<HealthSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const localTimeFormatter = useMemo(() => new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  }), []);

  const browserTimeZone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || "Local",
    [],
  );

  const formatLocalSlot = useCallback((slotHourUtc: number) => {
    const now = new Date();
    const date = new Date(Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      slotHourUtc,
      0,
      0,
      0,
    ));
    return localTimeFormatter.format(date);
  }, [localTimeFormatter]);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke<HealthStatusPayload>("base44-health-check", {
        body: { action: "status" },
      });
      if (error) throw error;
      if (data?.ok === false) throw new Error(data.error ?? "Provider checks did not load");
      setSlots(data?.slots ?? []);
    } catch (error) {
      toast({
        title: "Unable to load Provider checks",
        description: error instanceof Error ? providerText(error.message) : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const saveSlot = async (slot: HealthSlot, patch: { checkEnabled?: boolean; positiveEnabled?: boolean }) => {
    const checkEnabled = patch.checkEnabled ?? slot.check_enabled;
    const positiveEnabled = checkEnabled ? (patch.positiveEnabled ?? slot.positive_enabled) : false;
    const key = `${slot.slot_index}:${Object.keys(patch).join(",")}`;
    setSavingKey(key);
    try {
      const { data, error } = await supabase.functions.invoke<HealthStatusPayload>("base44-health-check", {
        body: {
          action: "save_slot_config",
          slotIndex: slot.slot_index,
          checkEnabled,
          positiveEnabled,
        },
      });
      if (error) throw error;
      if (data?.ok === false) throw new Error(data.error ?? "Provider check settings were not saved");
      setSlots(data?.slots ?? []);
      toast({ title: "Provider check updated" });
    } catch (error) {
      toast({
        title: "Unable to update Provider check",
        description: error instanceof Error ? providerText(error.message) : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-semibold text-foreground">
            <Radar className="h-5 w-5" />
            Provider Watchdog
          </h2>
          <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            {browserTimeZone}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => void loadStatus()} disabled={loading}>
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      <Card className="border-border/60">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-44">Slot</TableHead>
                <TableHead className="w-28 text-center">Check</TableHead>
                <TableHead className="w-32 text-center">Positive</TableHead>
                <TableHead className="min-w-56">Last Run</TableHead>
                <TableHead className="min-w-56">Recent History</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && slots.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="h-36 text-center text-sm text-muted-foreground">
                    <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                    Loading Provider checks...
                  </TableCell>
                </TableRow>
              )}

              {!loading && slots.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="h-36 text-center text-sm text-muted-foreground">
                    No Provider check slots found.
                  </TableCell>
                </TableRow>
              )}

              {slots.map((slot) => {
                const checkSaving = savingKey === `${slot.slot_index}:checkEnabled`;
                const positiveSaving = savingKey === `${slot.slot_index}:positiveEnabled`;
                const issue = runIssue(slot.last_run);
                return (
                  <TableRow key={slot.slot_index}>
                    <TableCell>
                      <div className="font-medium text-foreground">{formatLocalSlot(slot.slot_hour_utc)}</div>
                      <div className="mt-1 text-xs text-muted-foreground">{slot.slot_utc_label}</div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Checkbox
                        aria-label={`Enable ${slot.slot_utc_label} Provider check`}
                        checked={slot.check_enabled}
                        disabled={!!savingKey}
                        onCheckedChange={(checked) => void saveSlot(slot, { checkEnabled: checked === true })}
                      />
                      {checkSaving && <Loader2 className="ml-2 inline h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                    </TableCell>
                    <TableCell className="text-center">
                      <Checkbox
                        aria-label={`Post successful ${slot.slot_utc_label} Provider check`}
                        checked={slot.check_enabled && slot.positive_enabled}
                        disabled={!slot.check_enabled || !!savingKey}
                        onCheckedChange={(checked) => void saveSlot(slot, { positiveEnabled: checked === true })}
                      />
                      {positiveSaving && <Loader2 className="ml-2 inline h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                    </TableCell>
                    <TableCell>
                      {slot.last_run ? (
                        <div className="space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant="outline" className={cn("gap-1", statusClass(slot.last_run.status))}>
                              <StatusIcon status={slot.last_run.status} />
                              {statusLabel(slot.last_run.status)}
                            </Badge>
                            <span className="text-xs text-muted-foreground">{formatDate(slot.last_run.scheduled_for)}</span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {slackLabel(slot.last_run.slack_status)}
                            {slot.last_run.slack_error ? `: ${providerText(slot.last_run.slack_error)}` : ""}
                          </div>
                          {issue && (
                            <div className="max-w-md text-xs text-muted-foreground line-clamp-2">
                              {issue}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">Not run</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {slot.recent_runs.length > 0 ? (
                        <div className="space-y-1.5">
                          {slot.recent_runs.slice(0, 4).map((run) => (
                            <div key={run.id} className="flex flex-wrap items-center gap-2 text-xs">
                              <Badge variant="outline" className={cn("min-w-20 justify-center", statusClass(run.status))}>
                                {statusLabel(run.status)}
                              </Badge>
                              <span className="text-muted-foreground">{formatDate(run.scheduled_for)}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">No history</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
